import React, { memo, useCallback, useState } from "react";
import Spinner from "../../Spinner";
import MetricTrendChart from "../../charts/MetricTrendChart";
import CardiopulmonaryResultRow from "../rows/CardiopulmonaryResultRow";
import { useCardiopulmonaryResults } from "../../../hooks/results/useCardiopulmonaryResults";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_RESULTS_PAGE_SIZE,
  useInfiniteResultsPageSize,
} from "../../../hooks/results/useInfiniteResultsPageSize";
import { getValidatedMeasurementValue } from "../../../utils/results/validatedRanges";
import { SHOW_FULL_RESULTS } from "../../../config/results";
import { useResultRowNavigation } from "../../../hooks/results/useResultRowNavigation";
import { RESULT_MODE_CONFIG } from "../../../utils/results/resultModeConfig";
import ResultsTabState from "../ResultsTabState";
import { useDailyMetricTrend } from "../../../hooks/results/useDailyMetricTrend";

const CONFIG = RESULT_MODE_CONFIG.cardiopulmonary;

export default memo(function CardiopulmonaryResults({
  db,
  uid,
  firstRunByVariant,
}) {
  const { t, i18n } = useTranslation();

  const [metric, setMetric] = useState("HR"); // "HR" | "RR"
  const [pageSize, setPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);

  const resetKey = `${uid || ""}:${CONFIG.resetKey}`;

  const { rows, loading, loadingMore, hasMore, error } =
    useCardiopulmonaryResults(db, uid, {
      pageSize,
    });

  const { handleScroll } = useInfiniteResultsPageSize({
    canLoadMore: hasMore,
    loading,
    loadingMore,
    rowsLength: rows.length,
    resetKey,
    pageSize,
    setPageSize,
    initialPageSize: DEFAULT_RESULTS_PAGE_SIZE,
  });

  const onRowClick = useResultRowNavigation({
    variant: CONFIG.variant,
    context: CONFIG.context,
  });

  const getCardiopulmonaryTrendValue = useCallback(
    (row) =>
      getValidatedMeasurementValue(
        metric === "HR" ? "hr" : "rr",
        metric === "HR" ? row?.hr : row?.rr,
      ),
    [metric],
  );

  const trend = useDailyMetricTrend({
    rows,
    language: i18n.language,
    getValue: getCardiopulmonaryTrendValue,
  });

  const yUnit = metric === "HR" ? "bpm" : "bpm";
  const yDecimals = 0;
  const lineLabel =
    metric === "HR"
      ? t("results.cardio.hrAverage", "HR (Avg)")
      : t("results.cardio.rrAverage", "RR (Avg)");

  return (
    <ResultsTabState
      uid={uid}
      loading={loading}
      error={error}
      rows={rows}
      emptyMode={CONFIG.mode}
      firstRunByVariant={firstRunByVariant}
    >
      <div className="w-full h-full min-h-0 flex flex-col gap-2">
        {SHOW_FULL_RESULTS && (
          <>
            <div className="w-full flex gap-2 pt-2">
              <button
                type="button"
                className={[
                  "px-2 py-1 rounded-xl text-sm",
                  metric === "HR"
                    ? "bg-primary text-greyscale-1"
                    : "bg-greyscale-1 text-primary",
                ].join(" ")}
                onClick={() => setMetric("HR")}
              >
                {t("results.cardio.hr", "HR")}
              </button>

              <button
                type="button"
                className={[
                  "px-2 py-1 rounded-xl text-sm",
                  metric === "RR"
                    ? "bg-primary text-greyscale-1"
                    : "bg-greyscale-1 text-primary",
                ].join(" ")}
                onClick={() => setMetric("RR")}
              >
                {t("results.cardio.rr", "RR")}
              </button>
            </div>

            <MetricTrendChart
              height={190}
              labels={trend?.labels}
              values={trend?.values}
              xValues={trend?.xValues}
              xTickFormat="dd/MM"
              lineLabel={lineLabel}
              yUnit={yUnit}
              yDecimals={yDecimals}
              showMinMaxBars={true}
              minMax={trend?.minMax}
              counts={trend?.counts}
              showTrendLine={true}
            />
          </>
        )}

        <div
          aria-label="cardiopulmonary results list"
          className="w-full flex-1 min-h-0 overflow-y-auto"
          onScroll={handleScroll}
        >
          {rows.map((r, idx) => (
            <CardiopulmonaryResultRow
              key={r.id ?? idx}
              time={r.time}
              date={r.date}
              rr={r.rr}
              hr={r.hr}
              rrQuality={r.rrQuality}
              hrQuality={r.hrQuality}
              onClick={() => onRowClick(r)}
            />
          ))}
          {loadingMore ? <Spinner size={24} className="py-4" /> : null}
        </div>
      </div>
    </ResultsTabState>
  );
});
